// 2026-07-07: PMI Special-Process — Route Card Builder (the traveller).
// Assigns a route_template (e.g. Route 2198) to a real Work Order, issues a Route Card,
// and auto-spawns the special-process Followers (one per operation that binds an sp_recipe).
// Connects the flow: Work Order -> Route Card -> Followers -> operator capture.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { customerbasedshift, cleanCustomerId } from "../../Services/app/operatorservice";
import { shiftadd } from "../../Services/app/masterservice";
import { listWorkOrders } from "../../Services/app/workorderservice";
import Swal from 'sweetalert2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Tooltip, IconButton, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

const BR = {
  orange: '#f97316', blue: '#2563eb', green: '#16a34a', amber: '#f59e0b', red: '#dc2626',
  dark: '#111827', muted: '#6b7280', border: '#e5e7eb', bg: '#f9fafb',
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) + ' ' +
         dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const spOps = (rc) => (rc.operations || []).filter(o => o.special_process);

export default function RouteCardBuilder() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID') || window._env_.CUSTOMER_ID);
  const [cards, setCards] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [wos, setWos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issueOpen, setIssueOpen] = useState(false);
  const [travOpen, setTravOpen] = useState(false);
  const [trav, setTrav] = useState(null);
  const [draft, setDraft] = useState({ wo_id: '', route_no: '', route_card_no: '', issued_by: '' });
  const [busy, setBusy] = useState(false);

  const recipeByCode = useMemo(() => {
    const m = {}; recipes.forEach(r => { m[r.code] = r; }); return m;
  }, [recipes]);

  const refresh = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [rc, rt, sp, fr] = await Promise.all([
        customerbasedshift(customerId, 'route_cards').catch(() => []),
        customerbasedshift(customerId, 'route_templates').catch(() => []),
        customerbasedshift(customerId, 'sp_recipes').catch(() => []),
        customerbasedshift(customerId, 'follower_records').catch(() => []),
      ]);
      setCards(Array.isArray(rc?.[0]?.value) ? rc[0].value : []);
      setRoutes(Array.isArray(rt?.[0]?.value) ? rt[0].value : []);
      setRecipes(Array.isArray(sp?.[0]?.value) ? sp[0].value : []);
      setFollowers(Array.isArray(fr?.[0]?.value) ? fr[0].value : []);
      try {
        const res = await listWorkOrders(customerId);
        setWos(Array.isArray(res?.rows) ? res.rows : (Array.isArray(res) ? res : []));
      } catch { setWos([]); }
    } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { refresh(); }, [refresh]);

  const woById = useMemo(() => { const m = {}; wos.forEach(w => { m[w.id] = w; }); return m; }, [wos]);

  function openIssue() {
    setDraft({ wo_id: '', route_no: routes[0]?.route_no || '', route_card_no: '', issued_by: '' });
    setIssueOpen(true);
  }

  // auto-fill route card no from WO when picked
  function pickWo(wo_id) {
    const w = woById[wo_id];
    setDraft(d => ({ ...d, wo_id, route_card_no: d.route_card_no || (w ? `RC-${w.wo_number}` : '') }));
  }

  async function persistCards(next) { await shiftadd({ route_cards: next }, customerId, 'SERVER_SCOPE'); setCards(next); }
  async function persistFollowers(next) { await shiftadd({ follower_records: next }, customerId, 'SERVER_SCOPE'); setFollowers(next); }

  async function issue() {
    const w = woById[draft.wo_id];
    const route = routes.find(r => r.route_no === draft.route_no);
    if (!w || !route) { Swal.fire({ icon: 'warning', title: 'Pick a Work Order and a Route', confirmButtonColor: BR.orange }); return; }
    setBusy(true);
    try {
      const rc = {
        id: `rc-${Date.now()}`,
        route_card_no: draft.route_card_no || `RC-${w.wo_number}`,
        route_no: route.route_no, route_name: route.route_name,
        wo_id: w.id, wo_number: w.wo_number,
        part_no: w.part_number || '', part_rev: w.part_rev || '', qty: w.qty_ordered || '',
        issued_by: draft.issued_by || '', issued_ts: new Date().toISOString(),
        operations: JSON.parse(JSON.stringify(route.operations || [])),
        follower_ids: [], created_ts: new Date().toISOString(), updated_ts: '',
      };
      await persistCards([...cards, rc]);
      setIssueOpen(false);
      Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'success',
        title: `Route Card ${rc.route_card_no} issued (${spOps(rc).length} special-process ops)` });
      setTrav(rc); setTravOpen(true);   // jump straight to the traveller
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Issue failed', text: e.message, confirmButtonColor: BR.orange });
    } finally { setBusy(false); }
  }

  // spawn a follower per special-process op that doesn't already have one
  async function generateFollowers(rc) {
    setBusy(true);
    try {
      const existing = new Set(followers.map(f => f.id));
      const toAdd = [];
      const ids = [...(rc.follower_ids || [])];
      spOps(rc).forEach(op => {
        const fid = `flw-${rc.id}-${op.op_no}`;
        if (!ids.includes(fid)) ids.push(fid);
        if (existing.has(fid)) return;
        const recipe = recipeByCode[op.special_process];
        toAdd.push({
          id: fid,
          route_card_no: rc.route_card_no, wo_id: rc.wo_id, wo_number: rc.wo_number,
          part_no: rc.part_no, part_rev: rc.part_rev, qty: rc.qty,
          recipe_code: op.special_process, recipe_name: recipe?.name || op.special_process, commodity: recipe?.commodity || '',
          op_no: op.op_no, op_code: op.code, op_name: op.name,
          operator_name: '', operator_stamp: '', operator_level: '',
          rpg_no: '', batch_no: '', batch_qty: '', timer_id: '',
          status: 'OPEN', started_ts: '', completed_ts: '',
          readings: [], out_of_limit_count: 0, gate_fail_count: 0,
          qa_signoff_by: '', qa_signoff_ts: '', qa_disposition: '', qa_note: '',
          source_route_card: rc.id, created_ts: new Date().toISOString(), updated_ts: '',
        });
      });
      if (toAdd.length) await persistFollowers([...followers, ...toAdd]);
      const nextCards = cards.map(c => c.id === rc.id ? { ...c, follower_ids: ids, updated_ts: new Date().toISOString() } : c);
      await persistCards(nextCards);
      setTrav(t => t && t.id === rc.id ? { ...t, follower_ids: ids } : t);
      Swal.fire({ toast: true, position: 'top-end', timer: 2200, showConfirmButton: false, icon: 'success',
        title: toAdd.length ? `${toAdd.length} follower(s) generated — ready in Route Card Follower` : 'All followers already generated' });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Generate failed', text: e.message, confirmButtonColor: BR.orange });
    } finally { setBusy(false); }
  }

  const followerFor = (rc, op) => followers.find(f => f.id === `flw-${rc.id}-${op.op_no}`);

  const totals = useMemo(() => {
    let genf = 0, hold = 0;
    cards.forEach(c => { (c.follower_ids || []).forEach(fid => {
      const f = followers.find(x => x.id === fid); if (f) { genf++; if (f.status === 'HOLD') hold++; }
    }); });
    return { cards: cards.length, genf, hold };
  }, [cards, followers]);

  return (
    <div style={{ padding: 16, background: BR.bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: BR.dark }}>🗂️ Route Card Builder</h1>
          <div style={{ fontSize: 12, color: BR.muted, marginTop: 4 }}>
            Assign a route to a Work Order · issue the traveller · auto-spawn the special-process Followers
          </div>
        </div>
        <button onClick={openIssue}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: BR.orange, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
          <AddIcon fontSize="small" /> Issue Route Card
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Route cards issued', val: totals.cards, color: BR.blue },
          { label: 'Followers generated', val: totals.genf, color: BR.orange },
          { label: 'Quality holds', val: totals.hold, color: BR.amber },
        ].map(t => (
          <div key={t.label} style={{ background: '#fff', border: `2px solid ${BR.border}`, borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: t.color, lineHeight: 1.1, marginTop: 4 }}>{t.val}</div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', border: `1px solid ${BR.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted }}>Loading…</div>
        ) : cards.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: BR.muted, fontSize: 13 }}>
            No route cards yet. Click "Issue Route Card" to assign Route 2198 to a Work Order.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `1px solid ${BR.border}` }}>
                {['Route Card', 'WO', 'Part', 'Route', 'SP Ops', 'Followers', 'Issued', ''].map(h => (
                  <th key={h} style={{ padding: '9px 10px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cards.slice().sort((a, b) => new Date(b.issued_ts) - new Date(a.issued_ts)).map(rc => {
                const sp = spOps(rc);
                const gen = (rc.follower_ids || []).length;
                return (
                  <tr key={rc.id} style={{ borderBottom: `1px solid ${BR.border}` }}>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 800 }}>{rc.route_card_no}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700 }}>{rc.wo_number}</td>
                    <td style={{ padding: '9px 10px', fontFamily: 'monospace' }}>{rc.part_no || '—'}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ fontWeight: 700, color: BR.blue }}>{rc.route_no}</span>
                      <div style={{ fontSize: 10, color: BR.muted, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rc.route_name}</div>
                    </td>
                    <td style={{ padding: '9px 10px', fontWeight: 700 }}>{sp.length}</td>
                    <td style={{ padding: '9px 10px' }}>
                      <span style={{ fontWeight: 800, color: gen >= sp.length && sp.length ? BR.green : BR.orange }}>{gen}</span>
                      <span style={{ color: BR.muted }}>/{sp.length}</span>
                    </td>
                    <td style={{ padding: '9px 10px', fontSize: 11, color: BR.muted }}>{fmtDate(rc.issued_ts)}</td>
                    <td style={{ padding: '9px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Tooltip title="Open traveller"><IconButton size="small" onClick={() => { setTrav(rc); setTravOpen(true); }}>
                        <DescriptionIcon fontSize="small" sx={{ color: BR.blue }} />
                      </IconButton></Tooltip>
                      <Tooltip title="Generate followers"><IconButton size="small" onClick={() => generateFollowers(rc)} disabled={busy}>
                        <PlaylistAddCheckIcon fontSize="small" sx={{ color: BR.orange }} />
                      </IconButton></Tooltip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ---- Issue dialog ---- */}
      <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Issue Route Card</DialogTitle>
        <DialogContent dividers>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <TextField label="Work Order *" size="small" fullWidth select value={draft.wo_id}
              onChange={e => pickWo(e.target.value)} sx={{ gridColumn: '1 / span 2' }}>
              <MenuItem value="">— Select Work Order —</MenuItem>
              {wos.map(w => <MenuItem key={w.id} value={w.id}>{w.wo_number} · {w.part_number} · {w.status}</MenuItem>)}
            </TextField>
            <TextField label="Route *" size="small" fullWidth select value={draft.route_no}
              onChange={e => setDraft({ ...draft, route_no: e.target.value })} sx={{ gridColumn: '1 / span 2' }}>
              <MenuItem value="">— Select Route —</MenuItem>
              {routes.map(r => <MenuItem key={r.route_no} value={r.route_no}>{r.route_no} · {r.route_name}</MenuItem>)}
            </TextField>
            <TextField label="Route Card No" size="small" fullWidth value={draft.route_card_no}
              onChange={e => setDraft({ ...draft, route_card_no: e.target.value })} placeholder="1600090239" />
            <TextField label="Issued By" size="small" fullWidth value={draft.issued_by}
              onChange={e => setDraft({ ...draft, issued_by: e.target.value })} />
          </div>
          {draft.route_no && (
            <div style={{ marginTop: 12, fontSize: 11, color: BR.muted, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 10px' }}>
              {(() => { const r = routes.find(x => x.route_no === draft.route_no); const n = r ? (r.operations || []).filter(o => o.special_process).length : 0;
                return <span>This route has <strong>{r ? (r.operations || []).length : 0}</strong> operations · <strong>{n}</strong> special-process step(s) will spawn Followers.</span>; })()}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOpen(false)}>Cancel</Button>
          <Button onClick={issue} disabled={busy} variant="contained" sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
            {busy ? 'Issuing…' : 'Issue & open traveller'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Traveller dialog ---- */}
      <Dialog open={travOpen} onClose={() => setTravOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
          Route Card {trav?.route_card_no}
          <div style={{ fontSize: 12, color: BR.muted, fontWeight: 500, marginTop: 2 }}>
            WO {trav?.wo_number} · {trav?.part_no} {trav?.part_rev} · Qty {trav?.qty} · Route {trav?.route_no} — {trav?.route_name}
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: BR.bg, borderBottom: `2px solid ${BR.border}` }}>
                {['Op', 'Code', 'Operation', 'Internal Spec', 'Ref Docs', 'Follower'].map(h => (
                  <th key={h} style={{ padding: '7px 8px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: BR.muted }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(trav?.operations || []).map(op => {
                const isSp = !!op.special_process;
                const f = trav ? followerFor(trav, op) : null;
                return (
                  <tr key={op.op_no} style={{ borderBottom: `1px solid ${BR.border}`, background: isSp ? '#fff7ed' : '#fff' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 800, color: BR.muted }}>{op.op_no}</td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 700, color: isSp ? BR.orange : BR.dark }}>{op.code}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {op.name}
                      {isSp && <Chip label={op.special_process} size="small" sx={{ ml: 0.6, height: 17, fontSize: 9, fontWeight: 800, background: '#ffedd5', color: '#9a3412' }} />}
                    </td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, color: BR.muted }}>{op.internal_spec ? `${op.internal_spec}${op.spec_rev ? ' ' + op.spec_rev : ''}` : '—'}</td>
                    <td style={{ padding: '6px 8px', fontSize: 10, color: BR.muted }}>{(op.ref_docs || []).join(', ') || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>
                      {isSp
                        ? (f
                          ? <span style={{ fontSize: 10, fontWeight: 800, color: f.status === 'HOLD' ? BR.amber : f.status === 'DONE' ? BR.green : BR.orange }}>● {f.status}</span>
                          : <span style={{ fontSize: 10, color: BR.muted }}>not generated</span>)
                        : <span style={{ fontSize: 10, color: BR.muted }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <div style={{ fontSize: 11, color: BR.muted }}>
            Special-process ops (highlighted) spawn Followers that operators fill in the Route Card Follower screen.
          </div>
          <div>
            <Button onClick={() => setTravOpen(false)}>Close</Button>
            <Button onClick={() => trav && generateFollowers(trav)} disabled={busy} variant="contained" startIcon={<PlaylistAddCheckIcon />}
              sx={{ background: BR.orange, '&:hover': { background: '#ea580c' } }}>
              {busy ? 'Working…' : 'Generate Followers'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
}
